import * as pdfjsLib from "pdfjs-dist";
import { renderer } from "./pan-renderer";

export function init() {
    const ereaderEls = document.querySelectorAll(".js-pdf-reader");

    if (!ereaderEls) {
        return;
    }

    ereaderEls.forEach((el) => {
        let ereader = new PDFReader(el);

        ereader.setup();
    });
}

const DRAW_UPSCALE_FACTOR = 2;
const MAX_NUM_SCALING_STEPS = 3;
const RenderingStates = {
    INITIAL: 0,
    RUNNING: 1,
    PAUSED: 2,
    FINISHED: 3,
};

class PDFReader {
    constructor(ereader) {
        this.ereader = ereader;
        this.canvas = ereader.querySelector(".js-ereader-canvas");
        this.canvasContainer = ereader.querySelector(
            ".js-ereader-canvas-container"
        );
        this.renderContainer = ereader.querySelector(".js-ereader-container");
        this.pdf;
        this.id = ereader.dataset.id;
        this.fileId = ereader.dataset.fileId;

        this.pageNum = 1;
        this.pageRendering = false;
        this.pageNumPending = null;
        this.scale = 1;
        this.defaultScale = 1;
        this.minScale = 0.5;
        this.maxScale = 3;
        this.scaleInterval = 0.25;
        this.scaleZoomState = "idle";

        this.thumbnails = [];

        this.controls = {
            next: ereader.querySelector(".js-ereader-next"),
            prev: ereader.querySelector(".js-ereader-prev"),
            zoomIn: ereader.querySelector(".js-ereader-zoom-in"),
            zoomOut: ereader.querySelector(".js-ereader-zoom-out"),
            zoomReset: ereader.querySelector(".js-ereader-zoom-reset"),
            zoomInput: ereader.querySelector(".js-ereader-zoom-input"),
            fullscreen: ereader.querySelector(".js-ereader-fullscreen"),

            thumbnailMenuTrigger: ereader.querySelector(
                ".js-ereader-thumbnail-menu-trigger"
            ),
            fontControls: ereader.querySelector(".js-ereader-font-controls"),
            fontControlsTrigger: ereader.querySelector(
                ".js-ereader-font-controls-trigger"
            ),
        };

        this.presentationData = {
            title: ereader.querySelector(".js-ereader-title"),
            currentPagesStart: ereader.querySelector(
                ".js-ereader-current-pages-start"
            ),
            currentPagesEnd: ereader.querySelector(
                ".js-ereader-current-pages-end"
            ),
            currentPagesSeparator: ereader.querySelector(
                ".js-ereader-current-pages-separator"
            ),
            totalPages: ereader.querySelector(".js-ereader-total-pages"),
            progress: ereader.querySelector(".js-ereader-progress"),
            thumbnailMenu: ereader.querySelector(".js-ereader-thumbnail-menu"),
            thumbnailsContainer: ereader.querySelector(
                ".js-ereader-thumbnails"
            ),
        };
    }

    setup() {
        const _this = this;

        // Hide any controls we don't need for PDFs
        this.controls.fontControlsTrigger.classList.add("hidden");

        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.mjs`;

        // When the document has loaded, setup the PDF reader
        pdfjsLib.getDocument(this.ereader.dataset.file).promise.then((pdf) => {
            _this.pdf = pdf;

            // Check if we have a saved location
            const savedLocation = localStorage.getItem(
                `pdf-currentLocation-${this.fileId}`
            );

            if (savedLocation) {
                _this.pageNum = parseInt(savedLocation);
            }

            _this.calculateMetadata();

            _this.generateThumbnails();

            _this.setupNavigation();

            _this.setupZoom();

            _this.setupFullscreen();

            _this.renderPage(true);

            _this.setupWindowResize();
        });
    }

    setupWindowResize() {
        const _this = this;

        window.addEventListener("resize", () => {
            _this.scale = 1;
            _this.renderPage(true);
        });
    }

    calculateMetadata() {
        const _this = this;

        _this.pdf.getMetadata().then(function (data) {
            // Set Document Title
            _this.presentationData.title.textContent = data.info.Title ?? "";
        });
    }

    setProgress() {
        const _this = this;

        _this.totalPages = _this.pdf.numPages;
        _this.currentPagesStart = _this.pageNum;
        _this.currentPagesEnd = _this.pageNum;

        _this.progress = Math.round(
            (_this.currentPagesStart / _this.totalPages) * 100
        );

        // Set current pages
        _this.presentationData.currentPagesStart.innerText =
            _this.currentPagesStart;
        _this.presentationData.currentPagesEnd.innerText =
            _this.currentPagesEnd;

        // Hide end page if it's the same as the start page
        if (this.currentPagesStart === this.currentPagesEnd) {
            _this.presentationData.currentPagesEnd.classList.add("hidden");
            _this.presentationData.currentPagesSeparator.classList.add(
                "hidden"
            );
        } else {
            currentPagesEnd.classList.remove("hidden");
            currentPagesSeparator.classList.remove("hidden");
        }

        // Set total pages
        _this.presentationData.totalPages.innerText = _this.totalPages;

        // Set progress
        _this.presentationData.progress.innerText = `${_this.progress}%`;

        // Update navigation button visibliity
        if (_this.currentPagesStart >= _this.totalPages) {
            _this.controls.next.classList.add("hidden");
        } else {
            _this.controls.next.classList.remove("hidden");
        }

        if (_this.currentPagesStart <= 1) {
            _this.controls.prev.classList.add("hidden");
        } else {
            _this.controls.prev.classList.remove("hidden");
        }
    }

    setupFullscreen() {
        const _this = this,
            fullscreenBtn = this.ereader.querySelector(
                ".js-ereader-fullscreen"
            ),
            fullscreenIcon = this.ereader.querySelector(
                ".js-ereader-fullscreen-icon"
            ),
            fullscreenExitIcon = this.ereader.querySelector(
                ".js-ereader-fullscreen-exit-icon"
            );

        // If fullscreen available on the device, show the fullscreen button
        if (document.fullscreenEnabled) {
            fullscreenBtn.classList.remove("hidden");
        }

        _this.ereader.addEventListener("fullscreenchange", () => {
            if (document.fullscreenElement) {
                fullscreenIcon.classList.add("hidden");
                fullscreenExitIcon.classList.remove("hidden");

                // Add fullscreen class to container
                _this.renderContainer.classList.add(
                    "ereader__container--fullscreen"
                );
            } else {
                fullscreenIcon.classList.remove("hidden");
                fullscreenExitIcon.classList.add("hidden");

                _this.renderContainer.classList.remove(
                    "ereader__container--fullscreen"
                );
            }

            // Let the resize event finish before re-rendering the page
            setTimeout(() => {
                // Set to 1 to force a recalculation of the scale and sizes of elements
                _this.scale = 1;
                _this.renderPage(true);
            }, 1000);
        });

        fullscreenBtn.addEventListener("click", () => {
            if (!document.fullscreenElement) {
                _this.ereader.requestFullscreen();
            } else {
                document.exitFullscreen();
            }
        });
    }

    setupZoom() {
        const _this = this;

        // Zoom in
        _this.controls.zoomIn.addEventListener("click", () => {
            _this.scale =
                _this.scale >= _this.maxScale
                    ? _this.maxScale
                    : _this.roundToTwo(_this.scale + _this.scaleInterval);

            _this.setZoom();
        });

        // Zoom out
        _this.controls.zoomOut.addEventListener("click", () => {
            _this.scale =
                _this.scale <= _this.minScale
                    ? _this.minScale
                    : _this.roundToTwo(_this.scale - _this.scaleInterval);

            _this.setZoom();
        });

        // Reset zoom
        if (_this.controls.zoomReset) {
            _this.controls.zoomReset.addEventListener("click", () => {
                _this.scale = _this.defaultScale;
                _this.setZoom();
            });
        }

        // Zoom Custom Input
        if (_this.controls.zoomInput) {
            _this.controls.zoomInput.addEventListener("keyup", (e) => {
                e.stopPropagation();
            });

            _this.controls.zoomInput.addEventListener("change", (e) => {
                // Format inputted value
                let zoomVal = e.target.value;

                // If there's a % sign, remove it
                if (zoomVal.includes("%")) {
                    zoomVal = zoomVal.replace("%", "");
                }

                // Convert to decimal value
                zoomVal = zoomVal / 100 + _this.scaleDiff;

                // Check value is between min and max range
                if (zoomVal < _this.minScale) {
                    _this.scale = _this.minScale;
                } else if (zoomVal > _this.maxScale) {
                    _this.scale = _this.maxScale;
                } else {
                    _this.scale = zoomVal;
                }

                _this.setZoom();
            });
        }

        // Touch Events
        _this.renderContainer.addEventListener(
            "touchstart",
            (e) => {
                _this.onTouchStart(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "touchmove",
            (e) => {
                _this.onTouchMove(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "touchend",
            (e) => {
                _this.onTouchEnd(e);
            },
            {
                passive: false,
            }
        );

        // Pointer Events
        _this.renderContainer.addEventListener(
            "pointerdown",
            (e) => {
                _this.onPointerDown(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "pointermove",
            (e) => {
                _this.onPointerMove(e);
            },
            {
                passive: false,
            }
        );
        _this.renderContainer.addEventListener(
            "pointerup",
            (e) => {
                _this.onPointerUp(e);
            },
            {
                passive: false,
            }
        );
    }

    setZoom() {
        const _this = this;

        // If zoomed greater than defaultZoom,change cursor
        // on container to move
        if (_this.scale > _this.defaultScale) {
            _this.renderContainer.classList.add("cursor-move");
        } else {
            _this.renderContainer.classList.remove("cursor-move");
        }

        // Rest zoom/pan if scale is at 1
        if (_this.scale === _this.defaultScale) {
            // Disable zoom reset button
            if (_this.controls.zoomReset) {
                _this.controls.zoomReset.classList.remove("!inline-block");
            }
        } else {
            // Enable zoom reset button
            if (_this.controls.zoomReset) {
                _this.controls.zoomReset.classList.add("!inline-block");
            }
        }

        // Enable/Disable zoom buttons
        if (_this.scale !== _this.minScale) {
            // Enable zoom out button
            _this.controls.zoomOut.classList.remove(
                "opacity-40",
                "cursor-not-allowed"
            );
        } else {
            // Disable zoom out button
            _this.controls.zoomOut.classList.add(
                "opacity-40",
                "cursor-not-allowed"
            );
        }

        if (_this.scale !== _this.maxScale) {
            // Enable zoom in button
            _this.controls.zoomIn.classList.remove(
                "opacity-40",
                "cursor-not-allowed"
            );
        } else {
            // Disable zoom in button
            _this.controls.zoomIn.classList.add(
                "opacity-40",
                "cursor-not-allowed"
            );
        }

        _this.setZoomInput();

        _this.renderPage();
    }

    setZoomInput() {
        const _this = this;

        if (_this.controls.zoomInput) {
            _this.controls.zoomInput.value = `${Math.round(
                (_this.scale - _this.scaleDiff) * 100
            )}%`;
        }
    }

    getPinchDistance(event) {
        return Math.hypot(
            event.touches[0].pageX - event.touches[1].pageX,
            event.touches[0].pageY - event.touches[1].pageY
        );
    }

    getMidPoint(event) {
        return {
            x: (event.touches[0].pageX + event.touches[1].pageX) / 2,
            y: (event.touches[0].pageY + event.touches[1].pageY) / 2,
        };
    }

    onTouchStart(event) {
        const _this = this;
        const touch = event.changedTouches[0];

        _this.touchStartEvent = event;

        _this.startTouch = {
            x: touch.pageX,
            y: touch.pageY,
        };
    }

    onTouchMove(event) {
        const _this = this;

        // If the scale is not at the minimum, then pan
        if (_this.scale > _this.defaultScale) {
            event.preventDefault();

            const touch = event.changedTouches[0];

            const deltaX = touch.pageX - _this.startTouch.x;
            const deltaY = touch.pageY - _this.startTouch.y;

            _this.panInstance.panBy({ originX: deltaX, originY: deltaY });

            _this.startTouch.x = touch.pageX;
            _this.startTouch.y = touch.pageY;
        }
    }

    onTouchEnd(event) {
        const _this = this,
            swipeThreshold = 0.25;

        // If we're at the default/minscale and
        // this is a touch device then
        // a swipe should navigate the epub
        if (_this.scale <= _this.defaultScale) {
            // if (_this.scale === _this.minScale) {
            const bound = _this.renderContainer.getBoundingClientRect();
            const hr =
                (event.changedTouches[0].screenX -
                    _this.touchStartEvent.changedTouches[0].screenX) /
                bound.width;
            const vr = Math.abs(
                (event.changedTouches[0].screenY -
                    _this.touchStartEvent.changedTouches[0].screenY) /
                    bound.height
            );

            if (hr > swipeThreshold && vr < 0.1) {
                _this.rendition.prev();
            }

            if (hr < -swipeThreshold && vr < 0.1) {
                _this.rendition.next();
            }
        }
    }

    onPointerDown(event) {
        const _this = this;
    }

    onPointerMove(event) {
        const _this = this;

        if (event.pointerType === "touch") {
            return;
        }

        // If this isn't a touch device and there's no mouse button pressed
        // or the scale is at the minimum, then return
        if (event.buttons !== 1 || _this.scale === _this.minScale) {
            return;
        }

        if (event.movementX === 0 && event.movementY === 0) {
            return;
        }

        // If the scale is not at the minimum, then pan
        if (_this.scale > _this.defaultScale) {
            _this.panInstance.panBy({
                originX: event.movementX,
                originY: event.movementY,
            });
        }
    }

    onPointerEnd(event) {
        const _this = this;

        if (event.pointerType === "touch") {
            return;
        }

        _this.state = "idle";
    }

    onPointerUp(event) {
        const _this = this;

        if (event.pointerType === "touch") {
            return;
        }

        _this.onPointerEnd(event);
    }

    async ensurePdfPageLoaded(thumbView) {
        const _this = this;

        if (thumbView.pdfPage) {
            return thumbView.pdfPage;
        }
        try {
            const pdfPage = await _this.pdf.getPage(thumbView.id);

            if (!thumbView.pdfPage) {
                thumbView.setPdfPage(pdfPage);
            }

            return pdfPage;
        } catch (reason) {
            console.error("Unable to get page for thumb view", reason);
            return null;
        }
    }

    generateThumbnails() {
        const _this = this;

        // When the chapter menu button is clicked, toggle the menu
        _this.controls.thumbnailMenuTrigger.addEventListener("click", () => {
            _this.presentationData.thumbnailMenu.classList.toggle(
                "!translate-x-0"
            );
        });

        const firstPagePromise = _this.pdf.getPage(1);
        const optionalContentConfigPromise = _this.pdf.getOptionalContentConfig(
            {
                intent: "display",
            }
        );

        firstPagePromise
            .then((firstPdfPage) => {
                const pagesCount = _this.pdf.numPages;
                const viewport = firstPdfPage.getViewport({
                    scale: 1,
                });

                for (let pageNum = 1; pageNum <= pagesCount; ++pageNum) {
                    const thumbnail = new PDFThumbnail({
                        container: _this.presentationData.thumbnailsContainer,
                        id: pageNum,
                        defaultViewport: viewport.clone(),
                        optionalContentConfigPromise,
                        pdfReader: _this,
                    });

                    _this.ensurePdfPageLoaded(thumbnail).then(() => {
                        thumbnail.draw().catch((reason) => {
                            console.error(`thumbnail generation: "${reason}"`);
                        });
                    });

                    _this.thumbnails.push(thumbnail);
                }
            })
            .catch((reason) => {
                console.error(
                    `Unable to get page 1 for thumbnail generation: ${reason}`
                );
            });
    }

    renderPage(reset = false) {
        const _this = this;

        _this.pageRendering = true;

        _this.pdf.getPage(_this.pageNum).then((page) => {
            // On initial load we want the page to take up the full height of the container
            // so we need to calculate the scale range based on the container height
            if (reset) {
                _this.defaultScale = _this.roundToTwo(
                    _this.renderContainer.offsetHeight / page.view[3]
                );

                _this.scaleDiff = _this.defaultScale - _this.scale;
                _this.scale = _this.defaultScale;
                _this.maxScale = _this.roundToTwo(
                    _this.defaultScale + 8 * _this.scaleInterval
                );
                _this.minScale = _this.roundToTwo(
                    _this.defaultScale - 2 * _this.scaleInterval
                );

                _this.setZoomInput();
            }

            const viewport = page.getViewport({
                scale: _this.scale,
            });
            const canvas = _this.canvas;
            const context = canvas.getContext("2d");

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            page.render(renderContext).promise.then(() => {
                _this.pageRendering = false;

                if (_this.pageNumPending !== null) {
                    _this.pageNum = _this.pageNumPending;
                    _this.pageNumPending = null;
                }

                _this.panInstance = renderer({
                    container: _this.canvasContainer,
                    element: _this.canvas,
                });

                // Reset panning if scale is at default
                if (_this.scale === _this.defaultScale) {
                    _this.panInstance.reset();
                }
            });
        });

        _this.saveLocation();

        _this.setProgress();
    }

    setPage() {
        const _this = this;

        if (_this.pageRendering) {
            _this.pageNumPending = _this.pageNum;
        } else {
            _this.renderPage();
        }
    }

    nextPage() {
        const _this = this;

        if (_this.pageNum >= _this.totalPages) {
            return;
        }
        _this.pageNum++;
        _this.setPage();
    }

    prevPage() {
        const _this = this;

        if (_this.pageNum <= 1) {
            return;
        }

        _this.pageNum--;
        _this.setPage();
    }

    setupNavigation() {
        const _this = this;

        _this.controls.next.addEventListener("click", () => {
            _this.nextPage();
        });

        _this.controls.prev.addEventListener("click", () => {
            _this.prevPage();
        });

        // When left or right keys pressed, trigger page navigation
        // if reader is on screen
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        document.addEventListener("keyup", handleKeyUp);
                    } else {
                        document.removeEventListener("keyup", handleKeyUp);
                    }
                });
            },
            { threshold: 0.5 }
        );

        observer.observe(this.ereader);

        function handleKeyUp(e) {
            if (e.key === "ArrowRight") {
                _this.nextPage();
            } else if (e.key === "ArrowLeft") {
                _this.prevPage();
            }
        }
    }

    saveLocation() {
        const _this = this;

        // Save current location in local storage
        localStorage.setItem(
            `pdf-currentLocation-${_this.fileId}`,
            _this.pageNum
        );
    }

    // Utilities
    roundToTwo(num) {
        return +(Math.round(num + "e+2") + "e-2");
    }
}

class PDFThumbnail {
    constructor({
        container,
        id,
        defaultViewport,
        optionalContentConfigPromise,
        pdfReader,
    }) {
        this.id = id;
        this.renderingId = "thumbnail" + id;
        this.pageLabel = null;
        this.pdfPage = null;
        this.pdfReader = pdfReader;
        this.rotation = 0;
        this.viewport = defaultViewport;
        this.pdfPageRotate = defaultViewport.rotation;
        this._optionalContentConfigPromise =
            optionalContentConfigPromise || null;
        this.pageColors = null;
        this.enableHWA = false;
        this.renderTask = null;
        this.renderingState = RenderingStates.INITIAL;
        this.resume = null;

        const activeThumbnailClasses = ["ring-2", "ring-blue-500"];

        // Thumbnail Button
        const button = document.createElement("button");
        button.classList.add("transition-opacity", "hover:opacity-50");
        button.onclick = function () {
            pdfReader.pageNum = id;
            pdfReader.renderPage();

            // Remove active class from all thumbnails
            pdfReader.thumbnails.forEach((thumbnail) => {
                thumbnail.button.classList.remove(...activeThumbnailClasses);
            });

            // Add active class to current thumbnail
            button.classList.add(...activeThumbnailClasses);
            return false;
        };
        this.button = button;

        // Set active class on current thumbnail
        if (id === pdfReader.pageNum) {
            this.button.classList.add(...activeThumbnailClasses);
        }

        const div = document.createElement("div");
        div.className = "pdf-thumbnail";
        div.setAttribute("data-page-number", this.id);
        this.div = div;

        this.#updateDims();
        const img = document.createElement("div");
        img.className = "pdf-thumbnail-image";
        this._placeholderImg = img;
        div.append(img);
        button.append(div);
        container.append(button);
    }

    #updateDims() {
        const { width, height } = this.viewport;
        const ratio = width / height;
        this.canvasWidth = 100;
        this.canvasHeight = (this.canvasWidth / ratio) | 0;
        this.scale = this.canvasWidth / width;
        const { style } = this.div;
        style.setProperty("--thumbnail-width", `${this.canvasWidth}px`);
        style.setProperty("--thumbnail-height", `${this.canvasHeight}px`);
    }

    setPdfPage(pdfPage) {
        this.pdfPage = pdfPage;
        this.pdfPageRotate = pdfPage.rotate;
        const totalRotation = (this.rotation + this.pdfPageRotate) % 360;
        this.viewport = pdfPage.getViewport({
            scale: 1,
            rotation: totalRotation,
        });
        this.reset();
    }

    reset() {
        this.cancelRendering();
        this.renderingState = RenderingStates.INITIAL;
        this.div.removeAttribute("data-loaded");
        this.image?.replaceWith(this._placeholderImg);
        this.#updateDims();
        if (this.image) {
            this.image.removeAttribute("src");
            delete this.image;
        }
    }

    update({ rotation = null }) {
        if (typeof rotation === "number") {
            this.rotation = rotation;
        }
        const totalRotation = (this.rotation + this.pdfPageRotate) % 360;
        this.viewport = this.viewport.clone({
            scale: 1,
            rotation: totalRotation,
        });
        this.reset();
    }

    cancelRendering() {
        if (this.renderTask) {
            this.renderTask.cancel();
            this.renderTask = null;
        }
        this.resume = null;
    }

    #getPageDrawContext(upscaleFactor = 1, enableHWA = this.enableHWA) {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", {
            alpha: false,
            willReadFrequently: !enableHWA,
        });
        const outputScale = new pdfjsLib.OutputScale();
        canvas.width = (upscaleFactor * this.canvasWidth * outputScale.sx) | 0;
        canvas.height =
            (upscaleFactor * this.canvasHeight * outputScale.sy) | 0;
        const transform = outputScale.scaled
            ? [outputScale.sx, 0, 0, outputScale.sy, 0, 0]
            : null;
        return {
            ctx,
            canvas,
            transform,
        };
    }

    #convertCanvasToImage(canvas) {
        if (this.renderingState !== RenderingStates.FINISHED) {
            throw new Error(
                "#convertCanvasToImage: Rendering has not finished."
            );
        }
        const reducedCanvas = this.#reduceImage(canvas);
        const image = document.createElement("img");
        image.className = "thumbnailImage";
        image.setAttribute("data-l10n-id", "pdfjs-thumb-page-canvas");
        image.src = reducedCanvas.toDataURL();
        this.image = image;
        this.div.setAttribute("data-loaded", true);
        this._placeholderImg.replaceWith(image);
        reducedCanvas.width = 0;
        reducedCanvas.height = 0;
    }

    async #finishRenderTask(renderTask, canvas, error = null) {
        if (renderTask === this.renderTask) {
            this.renderTask = null;
        }
        if (error instanceof pdfjsLib.RenderingCancelledException) {
            return;
        }
        this.renderingState = RenderingStates.FINISHED;
        this.#convertCanvasToImage(canvas);
        if (error) {
            throw error;
        }
    }

    async draw() {
        if (this.renderingState !== RenderingStates.INITIAL) {
            console.error("Must be in new state before drawing");
            return undefined;
        }

        const { pdfPage } = this;
        if (!pdfPage) {
            this.renderingState = RenderingStates.FINISHED;
            throw new Error("pdfPage is not loaded");
        }

        this.renderingState = RenderingStates.RUNNING;

        const { ctx, canvas, transform } =
            this.#getPageDrawContext(DRAW_UPSCALE_FACTOR);
        const drawViewport = this.viewport.clone({
            scale: DRAW_UPSCALE_FACTOR * this.scale,
        });

        const renderContext = {
            canvasContext: ctx,
            transform,
            viewport: drawViewport,
            optionalContentConfigPromise: this._optionalContentConfigPromise,
            pageColors: this.pageColors,
        };
        const renderTask = (this.renderTask = pdfPage.render(renderContext));

        const resultPromise = renderTask.promise.then(
            () => this.#finishRenderTask(renderTask, canvas),
            (error) => this.#finishRenderTask(renderTask, canvas, error)
        );

        resultPromise.finally(() => {
            canvas.width = 0;
            canvas.height = 0;
        });

        return resultPromise;
    }

    setImage(pageView) {
        if (this.renderingState !== RenderingStates.INITIAL) {
            return;
        }

        const { thumbnailCanvas: canvas, pdfPage, scale } = pageView;

        if (!canvas) {
            return;
        }

        if (!this.pdfPage) {
            this.setPdfPage(pdfPage);
        }

        if (scale < this.scale) {
            return;
        }

        this.renderingState = RenderingStates.FINISHED;
        this.#convertCanvasToImage(canvas);
    }

    #reduceImage(img) {
        const { ctx, canvas } = this.#getPageDrawContext(1, true);

        if (img.width <= 2 * canvas.width) {
            ctx.drawImage(
                img,
                0,
                0,
                img.width,
                img.height,
                0,
                0,
                canvas.width,
                canvas.height
            );
            return canvas;
        }

        let reducedWidth = canvas.width << MAX_NUM_SCALING_STEPS;
        let reducedHeight = canvas.height << MAX_NUM_SCALING_STEPS;
        const [reducedImage, reducedImageCtx] = TempImageFactory.getCanvas(
            reducedWidth,
            reducedHeight
        );

        while (reducedWidth > img.width || reducedHeight > img.height) {
            reducedWidth >>= 1;
            reducedHeight >>= 1;
        }

        reducedImageCtx.drawImage(
            img,
            0,
            0,
            img.width,
            img.height,
            0,
            0,
            reducedWidth,
            reducedHeight
        );

        while (reducedWidth > 2 * canvas.width) {
            reducedImageCtx.drawImage(
                reducedImage,
                0,
                0,
                reducedWidth,
                reducedHeight,
                0,
                0,
                reducedWidth >> 1,
                reducedHeight >> 1
            );
            reducedWidth >>= 1;
            reducedHeight >>= 1;
        }

        ctx.drawImage(
            reducedImage,
            0,
            0,
            reducedWidth,
            reducedHeight,
            0,
            0,
            canvas.width,
            canvas.height
        );

        return canvas;
    }
}
